/* eslint-disable no-prototype-builtins */
/* eslint-disable react/prop-types */
import { useEffect, useCallback, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Checkbox,
  Typography,
  Paper,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import Select from "react-select";
import { useTheme } from "@mui/material/styles";
import { getSingleSelectStyles, getMultiSelectStyles } from "@/lib/styles";
import FileUploadField from "@/components/FileUploadField";
import { useTranslation } from "react-i18next";

const parseBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
};

const StepInputsForm = ({
  setFileInputValues,
  fileInputValues,
  setInputValues,
  inputValues,
  stepInputs = [],
  onChange,
  userList = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [touched, setTouched] = useState({});
  const singleSelectStyles = useMemo(
    () => getSingleSelectStyles(theme),
    [theme]
  );
  const multiSelectStyles = useMemo(() => getMultiSelectStyles(theme), [theme]);

  // Initialize dynamic fields when stepInputs change
  useEffect(() => {
    if (!stepInputs.length) return;

    const ids = stepInputs.map((s) => s.id);
    const hasAllKeys = ids.every((id) =>
      Object.prototype.hasOwnProperty.call(inputValues, id)
    );
    if (hasAllKeys && Object.keys(inputValues).length === ids.length) return;

    const initialValues = stepInputs.reduce((acc, input) => {
      const type = (input.input_type || "").toUpperCase();
      let value;

      switch (type) {
        case "BOOLEAN":
          // Boolean: Use default_value if provided, otherwise "NO" (false)
          if (input.hasOwnProperty("default_value") && input.default_value) {
            // Convert various default value formats to "YES" or "NO"
            const defaultVal = String(input.default_value).toUpperCase();
            if (
              defaultVal === "YES" ||
              defaultVal === "TRUE" ||
              defaultVal === "1"
            ) {
              value = "YES";
            } else {
              value = "NO"; // Default to NO for any other value
            }
          } else {
            value = "NO"; // No default value - start with NO
          }
          break;
        case "CONFIRMATION":
          // Confirmation stores boolean
          value = input.hasOwnProperty("default_value")
            ? parseBoolean(input.default_value)
            : false;
          break;
        case "MULTI_USER_SELECTION":
        case "MULTI_SELECTION":
          value = input.hasOwnProperty("default_value")
            ? Array.isArray(input.default_value)
              ? input.default_value
              : input.default_value
                ? input.default_value.split(",").filter(Boolean)
                : []
            : [];
          break;
        case "SELECTION":
        case "USER_SELECTION":
          value = input.hasOwnProperty("default_value")
            ? input.default_value
            : "";
          break;
        default:
          value = input.hasOwnProperty("default_value")
            ? input.default_value
            : "";
          break;
      }

      acc[input.id] = value;
      return acc;
    }, {});

    setInputValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepInputs, setInputValues]);

  const handleInputChange = useCallback(
    (id, value) => {
      setInputValues((prev) => ({
        ...prev,
        [id]: value,
      }));
      onChange(id, value);
    },
    [setInputValues, onChange]
  );

  const handleFileInputChange = useCallback(
    (id, files) => {
      const fileArray = Array.from(files);
      setFileInputValues((prev) => ({
        ...prev,
        [id]: fileArray,
      }));
      onChange(id, fileArray);
    },
    [setFileInputValues, onChange]
  );

  // Handle blur to mark fields as touched
  const handleBlur = useCallback((id) => {
    setTouched((prev) => ({ ...prev, [id]: true }));
  }, []);

  // Validate a single field
  const validateField = useCallback((input, value, files) => {
    const type = (input.input_type || "").toUpperCase();

    // CONFIRMATION is ALWAYS required (must be checked)
    if (type === "CONFIRMATION") {
      return value === true;
    }

    // For other types, check if field is required
    if (!input.required) return true;

    switch (type) {
      case "BOOLEAN":
        // Boolean must have a selection (YES or NO) if required
        return value === "YES" || value === "NO";
      case "MULTI_USER_SELECTION":
      case "MULTI_SELECTION":
        return Array.isArray(value) && value.length > 0;
      case "FILE_UPLOAD":
        return Array.isArray(files) && files.length > 0;
      case "SHORT_TEXT":
      case "TEXT":
      case "NUMERIC":
      case "TIMESTAMP":
      case "SELECTION":
      case "USER_SELECTION":
        return value && String(value).trim() !== "";
      default:
        return true;
    }
  }, []);

  // Transform options array of strings into react-select compatible format
  // eslint-disable-next-line no-unused-vars
  const getSelectOptions = useCallback((options, inputName) => {
    if (!Array.isArray(options)) {
      return [];
    }
    return options.map((option) => ({
      value: option,
      label: option,
    }));
  }, []);

  const renderField = useCallback(
    (input) => {
      const type = (input.input_type || "").toUpperCase();
      const required = input.required || false;
      // Use translated label if available, otherwise fall back to input.display_name or input.name
      const label = t(`stepInputsForm.${input.id}.name`, {
        defaultValue: input.display_name || input.name || "",
      });

      // Check validation status (CONFIRMATION is always required)
      const isFieldRequired = type === "CONFIRMATION" || required;
      const hasError =
        touched[input.id] &&
        isFieldRequired &&
        !validateField(input, inputValues[input.id], fileInputValues[input.id]);
      const isValid = touched[input.id] && !hasError && isFieldRequired;

      // Field wrapper with validation icons
      const fieldWrapper = (content) => (
        <Card
          key={input.id}
          sx={{
            mb: 2,
            border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
            bgcolor: theme.palette.checklist.surface.elevated,
            borderRadius: 2,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              borderColor: hasError
                ? theme.palette.error.main
                : theme.palette.checklist.border.focus,
              boxShadow: theme.shadows[1],
            },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {label}
                {required && (
                  <span style={{ color: theme.palette.error.main }}> *</span>
                )}
              </Typography>
              {isValid && (
                <CheckCircleIcon
                  sx={{
                    ml: 1,
                    fontSize: 16,
                    color: theme.palette.success.main,
                  }}
                />
              )}
              {hasError && (
                <WarningIcon
                  sx={{
                    ml: 1,
                    fontSize: 16,
                    color: theme.palette.error.main,
                  }}
                />
              )}
            </Box>
            {content}
          </CardContent>
        </Card>
      );

      switch (type) {
        case "SHORT_TEXT":
        case "TEXT":
          return fieldWrapper(
            <TextField
              fullWidth
              multiline
              rows={input.input_type === "TEXT" ? 3 : 1}
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={required}
              error={hasError}
              helperText={
                hasError
                  ? t("stepInputsForm.errorRequired", { field: label })
                  : ""
              }
              placeholder={t("stepInputsForm.textFieldPlaceholder")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "NUMERIC":
          return fieldWrapper(
            <TextField
              fullWidth
              type="number"
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={required}
              error={hasError}
              helperText={
                hasError
                  ? t("stepInputsForm.errorRequired", { field: label })
                  : ""
              }
              placeholder={t("stepInputsForm.numericFieldPlaceholder")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "BOOLEAN":
          return (
            <Card
              key={input.id}
              sx={{
                mb: 2,
                border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
                bgcolor: theme.palette.checklist.surface.elevated,
                borderRadius: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: hasError
                    ? theme.palette.error.main
                    : theme.palette.checklist.border.focus,
                  boxShadow: theme.shadows[1],
                },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  {/* Left side: Label with validation icons */}
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {label}
                      {required && (
                        <span style={{ color: theme.palette.error.main }}>
                          {" "}
                          *
                        </span>
                      )}
                    </Typography>
                    {isValid && (
                      <CheckCircleIcon
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    )}
                    {hasError && (
                      <WarningIcon
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>

                  {/* Right side: YES/NO toggle buttons */}
                  <ToggleButtonGroup
                    value={inputValues[input.id] || null}
                    exclusive
                    onChange={(e, newValue) => {
                      handleInputChange(input.id, newValue);
                    }}
                    onBlur={() => handleBlur(input.id)}
                    sx={{ gap: 0.5 }}
                  >
                    <ToggleButton
                      value="YES"
                      aria-label="yes"
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: 3,
                        fontWeight: 500,
                        textTransform: "none",
                        minWidth: 60,
                        fontSize: "0.8125rem",
                        transition: "all 0.3s ease",
                        border: `1px solid ${theme.palette.checklist.border.subtle}`,
                        bgcolor:
                          inputValues[input.id] === "YES"
                            ? theme.palette.success.dark
                            : theme.palette.checklist.surface.elevated,
                        color:
                          inputValues[input.id] === "YES"
                            ? theme.palette.common.white
                            : theme.palette.text.primary,
                        "&:hover": {
                          bgcolor: theme.palette.success.main,
                          color: theme.palette.common.white,
                        },
                        "&.Mui-selected": {
                          bgcolor: theme.palette.success.dark,
                          color: theme.palette.common.white,
                          boxShadow: theme.shadows[2],
                          transform: "scale(1.05)",
                          borderColor: theme.palette.checklist.border.focus,
                          "&:hover": {
                            bgcolor: theme.palette.success.main,
                          },
                        },
                      }}
                    >
                      {t("stepInputsForm.yes", "YES")}
                    </ToggleButton>
                    <ToggleButton
                      value="NO"
                      aria-label="no"
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: 3,
                        fontWeight: 500,
                        textTransform: "none",
                        minWidth: 60,
                        fontSize: "0.8125rem",
                        transition: "all 0.3s ease",
                        border: `1px solid ${theme.palette.checklist.border.subtle}`,
                        bgcolor:
                          inputValues[input.id] === "NO"
                            ? theme.palette.error.dark
                            : theme.palette.checklist.surface.elevated,
                        color:
                          inputValues[input.id] === "NO"
                            ? theme.palette.common.white
                            : theme.palette.text.primary,
                        "&:hover": {
                          bgcolor: theme.palette.error.main,
                          color: theme.palette.common.white,
                        },
                        "&.Mui-selected": {
                          bgcolor: theme.palette.error.dark,
                          color: theme.palette.common.white,
                          boxShadow: theme.shadows[2],
                          transform: "scale(1.05)",
                          borderColor: theme.palette.checklist.border.focus,
                          "&:hover": {
                            bgcolor: theme.palette.error.main,
                          },
                        },
                      }}
                    >
                      {t("stepInputsForm.no", "NO")}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {/* Description below if exists */}
                {input.description && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    {input.description}
                  </Typography>
                )}

                {/* Error message */}
                {hasError && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "error.main", display: "block" }}
                  >
                    {t("stepInputsForm.errorRequired", { field: label })}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        case "CONFIRMATION":
          return (
            <Card
              key={input.id}
              sx={{
                mb: 2,
                border: `1px solid ${hasError ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
                bgcolor: theme.palette.checklist.surface.elevated,
                borderRadius: 2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: hasError
                    ? theme.palette.error.main
                    : theme.palette.checklist.border.focus,
                  boxShadow: theme.shadows[1],
                },
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  {/* Left side: Label with validation icons */}
                  <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {label}
                      <span style={{ color: theme.palette.error.main }}>
                        {" "}
                        * {/* CONFIRMATION is always required */}
                      </span>
                    </Typography>
                    {isValid && (
                      <CheckCircleIcon
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          color: theme.palette.success.main,
                        }}
                      />
                    )}
                    {hasError && (
                      <WarningIcon
                        sx={{
                          ml: 1,
                          fontSize: 16,
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                  </Box>

                  {/* Right side: Checkbox */}
                  <Checkbox
                    checked={Boolean(inputValues[input.id])}
                    onChange={(e) =>
                      handleInputChange(input.id, e.target.checked)
                    }
                    onBlur={() => handleBlur(input.id)}
                    sx={{
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>

                {/* Description below if exists */}
                {input.description && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    {input.description}
                  </Typography>
                )}

                {/* Error message */}
                {hasError && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: "error.main", display: "block" }}
                  >
                    {t("stepInputsForm.errorRequired", { field: label })}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        case "TIMESTAMP":
          return fieldWrapper(
            <TextField
              fullWidth
              type="datetime-local"
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={required}
              slotProps={{ inputLabel: { shrink: true } }}
              error={hasError}
              helperText={
                hasError
                  ? t("stepInputsForm.errorRequired", { field: label })
                  : ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "USER_SELECTION":
          return fieldWrapper(
            <Select
              options={userList}
              value={
                userList.find((opt) => opt.value === inputValues[input.id]) ||
                null
              }
              onChange={(selected) =>
                handleInputChange(input.id, selected ? selected.value : "")
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("stepInputsForm.selectUserPlaceholder")}
              isClearable={true}
              styles={{
                ...singleSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: hasError
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              required={required}
            />
          );
        case "MULTI_USER_SELECTION":
          return fieldWrapper(
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={userList}
              value={
                Array.isArray(inputValues[input.id])
                  ? inputValues[input.id]
                      .map((val) => userList.find((opt) => opt.value === val))
                      .filter(Boolean)
                  : []
              }
              onChange={(selectedOptions) =>
                handleInputChange(
                  input.id,
                  selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                )
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("stepInputsForm.selectUsersPlaceholder")}
              styles={{
                ...multiSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: hasError
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              required={required}
            />
          );
        case "SELECTION": {
          const options = getSelectOptions(input.options, input.name);
          if (!options.length) {
            return fieldWrapper(
              <Typography color="error" variant="body2">
                {t("stepInputsForm.noOptions", { field: label })}
              </Typography>
            );
          }
          return fieldWrapper(
            <Select
              options={options}
              value={
                options.find((opt) => opt.value === inputValues[input.id]) ||
                null
              }
              onChange={(selected) =>
                handleInputChange(input.id, selected ? selected.value : "")
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("stepInputsForm.selectOptionPlaceholder")}
              isClearable={true}
              styles={{
                ...singleSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: hasError
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isDisabled={!options.length}
              required={required}
            />
          );
        }
        case "MULTI_SELECTION": {
          const options = getSelectOptions(input.options, input.name);
          if (!options.length) {
            return fieldWrapper(
              <Typography color="error" variant="body2">
                {t("stepInputsForm.noOptions", { field: label })}
              </Typography>
            );
          }
          return fieldWrapper(
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={options}
              value={
                Array.isArray(inputValues[input.id])
                  ? inputValues[input.id]
                      .map((val) => options.find((opt) => opt.value === val))
                      .filter(Boolean)
                  : []
              }
              onChange={(selectedOptions) =>
                handleInputChange(
                  input.id,
                  selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                )
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("stepInputsForm.selectOptionsPlaceholder")}
              styles={{
                ...multiSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: hasError
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isDisabled={!options.length}
              required={required}
            />
          );
        }
        case "FILE_UPLOAD": {
          return fieldWrapper(
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: theme.palette.checklist.surface.primary,
                border: `2px dashed ${hasError ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
                borderRadius: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: hasError
                    ? theme.palette.error.main
                    : theme.palette.checklist.border.focus,
                },
              }}
            >
              <FileUploadField
                multiple={true}
                accept="image/*,application/pdf"
                label={
                  t(`stepInputsForm.${input.id}.name`, {
                    defaultValue: input.display_name || input.name || "",
                  }) || t("stepInputsForm.uploadFilesLabel")
                }
                inputId={`file-upload-${input.id}`}
                fileNames={(fileInputValues[input.id] || []).map((f) => f.name)}
                onChange={(e) =>
                  handleFileInputChange(input.id, e.target.files)
                }
                onDelete={(fileIndex) => {
                  const updatedFiles = fileInputValues[input.id].filter(
                    (_, idx) => idx !== fileIndex
                  );
                  setFileInputValues((prev) => ({
                    ...prev,
                    [input.id]: updatedFiles,
                  }));
                  onChange(input.id, updatedFiles);
                  // Mark as touched and trigger validation
                  handleBlur(input.id);
                }}
                required={required}
              />
            </Paper>
          );
        }
        default: {
          return fieldWrapper(
            <Typography color="error" variant="body2">
              {t("stepInputsForm.unsupportedInputType", {
                type: input.input_type,
              })}
            </Typography>
          );
        }
      }
    },
    [
      inputValues,
      fileInputValues,
      touched,
      handleInputChange,
      handleFileInputChange,
      handleBlur,
      validateField,
      userList,
      singleSelectStyles,
      multiSelectStyles,
      getSelectOptions,
      t,
      setFileInputValues,
      onChange,
      theme,
    ]
  );

  const sortedStepInputs = useMemo(
    () => [...stepInputs].sort((a, b) => a.position - b.position),
    [stepInputs]
  );

  if (!stepInputs.length) return null;

  return (
    <Box>
      <Box display="flex" flexDirection="column" gap={2}>
        {sortedStepInputs.map((input) => (
          <Box key={input.id}>{renderField(input)}</Box>
        ))}
      </Box>
    </Box>
  );
};

export default StepInputsForm;
